import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Trash2, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { adminListReports, adminDeletePrayer } from '../lib/api';
import { useConfirmDialog } from '../components/ConfirmDialog';

/**
 * Admin Reports triage page (`/admin/reports`). Only accessible to users
 * whose backend `role === 'admin'`. Lists every reported prayer with the
 * report count, the latest reason, and a "Delete Content" button that
 * soft-deletes the prayer and clears the associated report rows.
 */
const AdminReports = () => {
  const { user, loading: authLoading } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const data = await adminListReports();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (user?.role === 'admin') load(); }, [user]);

  const handleDelete = async (prayerId) => {
    const ok = await confirm({
      title: 'Delete this reported prayer?',
      description: 'The prayer will be permanently removed and its report entries cleared. This cannot be undone.',
      confirmText: 'Delete Content',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(prayerId);
    try {
      await adminDeletePrayer(prayerId);
      setRows((prev) => prev.filter((r) => r.prayer_id !== prayerId));
    } catch (e) {
      alert(e?.response?.data?.detail || e.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  if (authLoading) {
    return <div className="max-w-4xl mx-auto p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /> Loading…</div>;
  }
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center" data-testid="admin-not-signed-in">
        <p className="text-zinc-600 dark:text-zinc-300 mb-3">Please sign in to access admin tools.</p>
      </div>
    );
  }
  if (user.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center" data-testid="admin-forbidden">
        <ShieldAlert size={40} className="mx-auto text-red-500 mb-3" />
        <p className="text-zinc-600 dark:text-zinc-300">You don&apos;t have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="admin-reports-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
          <ShieldAlert size={22} className="text-red-500" />
          Reported Prayers
        </h1>
        <button
          type="button"
          onClick={load}
          data-testid="admin-refresh-btn"
          className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-zinc-300 dark:border-zinc-700 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <div className="text-zinc-500"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading reports…</div>}
      {err && <div className="text-red-600 dark:text-red-400 text-sm border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 rounded-lg px-3 py-2">{err}</div>}
      {!loading && !err && rows.length === 0 && (
        <div className="text-zinc-500 dark:text-zinc-400 text-center py-16" data-testid="admin-reports-empty">
          Nothing to review — no reported prayers.
        </div>
      )}

      <ul className="space-y-3">
        {rows.map((r) => (
          <li
            key={r.prayer_id}
            data-testid="admin-report-row"
            data-prayer-id={r.prayer_id}
            className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/prayer/${r.prayer_id}`}
                    className="font-semibold text-zinc-900 dark:text-white hover:underline truncate"
                  >
                    {r.prayer?.title || '(untitled)'}
                  </Link>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300 font-medium">
                    {r.report_count} report{r.report_count === 1 ? '' : 's'}
                  </span>
                  {r.prayer?.is_deleted && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-800 text-zinc-600">deleted</span>
                  )}
                </div>
                {r.prayer?.author_name && (
                  <div className="text-xs text-zinc-500 mt-0.5">
                    by {r.prayer.author_name}{r.prayer.author_handle ? ` · @${r.prayer.author_handle}` : ''}
                  </div>
                )}
                {r.prayer?.description && (
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 line-clamp-3 whitespace-pre-wrap">
                    {r.prayer.description}
                  </p>
                )}
                {r.latest_reason && (
                  <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 border-l-2 border-zinc-300 dark:border-zinc-700 pl-2 italic">
                    Latest reason: {r.latest_reason}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDelete(r.prayer_id)}
                disabled={deletingId === r.prayer_id || r.prayer?.is_deleted}
                data-testid="admin-delete-btn"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                {deletingId === r.prayer_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete Content
              </button>
            </div>
          </li>
        ))}
      </ul>
      {confirmDialog}
    </div>
  );
};

export default AdminReports;
